import { useState } from "react";
import {
    AnnualWheelActivityTemplatesViewModel,
    AnnualWheelActivityTemplateTableModel,
    AnnualWheelActivityTemplateViewModel,
    TemplateActivityPaneProps,
} from "./TemplateActivityPane.types.ts";
import { useTranslation } from "../../localization/useTranslation.ts";
import { isNullOrWhitespace } from "../../common/stringOperations.ts";

import { useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";
import { useFilterState } from "@common/hooks/useFilterState";
import { FilterOption, FilterOptionType } from "@common/components/filterPopover/FilterPopover.types";
import { applyClientFilters, withOperators } from "@common/components/filterBar/filterOperatorMatching";
import { useIsPaneOpen, usePanes, useRefetchWhenPaneSaves } from "../../main/panes/usePanes";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import ActivityPane from "../activityPane/ActivityPane";
import { SelectedAnnualWheelActivityTemplate } from "../AnnualWheelActivity.types";

// A template without a type is picked through its own option, as a template's type is otherwise its enum value
const noTypeFilterId = "null";

export function useTemplateActivityPane(props: TemplateActivityPaneProps) {
    const { translateString, translateAnnualWheelActivityGeneralTypeEnum } = useTranslation();
    const { customerId } = useUserContext();
    const { open: openPane } = usePanes();

    const [selectedTemplateId, setSelectedTemplateId] = useState<string | undefined>(undefined);
    // The row stays marked only while its activity is still open beside the templates, so a created or
    // cancelled activity does not leave a row looking chosen.
    const isSelectedTemplateOpen = useIsPaneOpen(ActivityPane, (activityProps) => getTemplateId(activityProps.template) === selectedTemplateId);

    const isCreateFromObligationTemplates = !isNullOrWhitespace(props.contractId);
    const { selectFilters, filterOperators, onFilterChange, pinnedFilterIds, onPinnedFilterIdsChange } = useFilterState(
        isCreateFromObligationTemplates ? `contract-${props.contractId}-obligation-templates` : `annual-wheel-templates-${customerId}`
    );

    let url = `/annualWheelActivityTemplate`;
    if (isCreateFromObligationTemplates) {
        url = `/annualWheelActivityTemplate/${props.contractId}/obligations`;
    }
    const { isLoading, data, refetch } = useDotLegalQuery<AnnualWheelActivityTemplatesViewModel>(url);

    // The templates stay open while activities are created from them, and each one changes the planned column.
    useRefetchWhenPaneSaves(refetch);

    const selectTemplate = (selected: AnnualWheelActivityTemplateViewModel) => {
        setSelectedTemplateId(selected.id);
        openPane(ActivityPane, {
            mode: "edit",
            contractId: props.contractId,
            template: toSelectedTemplate(selected, isCreateFromObligationTemplates),
            startDate: props.startDate,
        });
    };

    return {
        isLoading,
        rows: convertRowsToTableData(getFilteredData(data?.rows ?? [])),
        isCreateFromObligationTemplates,
        selectedTemplateId: isSelectedTemplateOpen ? selectedTemplateId : undefined,
        selectTemplate,
        filterOptions: getFilterOptions(),
        onFilterChange,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
    };

    function getFilterOptions(): Array<FilterOption> {
        const complianceAreaAndPlannedFilters: Array<FilterOption> = [
            {
                id: "complianceAreas",
                label: translateString("complianceAreas"),
                type: FilterOptionType.SelectField,
                value: selectFilters.complianceAreas ?? [],
                fixedData: data?.selectableComplianceAreas ?? [],
            },
            {
                id: "isPlanned",
                label: translateString("plannedInAnnualWheel"),
                type: FilterOptionType.SelectField,
                value: selectFilters.isPlanned ?? [],
                fixedData: [
                    { id: "1", name: translateString("plannedSingular") },
                    { id: "0", name: translateString("notPlanned") },
                ],
            },
        ];

        if (isCreateFromObligationTemplates) {
            return withOperators(complianceAreaAndPlannedFilters, filterOperators);
        }

        return withOperators(
            [
                {
                    id: "frameworks",
                    label: translateString("framework"),
                    type: FilterOptionType.SelectField,
                    value: selectFilters.frameworks ?? [],
                    fixedData: data?.selectableMappedFrameworks ?? [],
                },
                {
                    id: "types",
                    label: translateString("type"),
                    type: FilterOptionType.SelectField,
                    value: selectFilters.types ?? [],
                    fixedData: getSelectableTypes(),
                },
                ...complianceAreaAndPlannedFilters,
                {
                    id: "isCustom",
                    label: translateString("templateType"),
                    type: FilterOptionType.SelectField,
                    value: selectFilters.isCustom ?? [],
                    fixedData: [
                        { id: "1", name: translateString("ownTemplates") },
                        { id: "0", name: translateString("defaultTemplates") },
                    ],
                },
            ],
            filterOperators
        );
    }

    function getSelectableTypes() {
        const selectableTypes = (data?.selectableTypes ?? []).map((x) => ({
            id: x.toString(),
            name: translateAnnualWheelActivityGeneralTypeEnum(x.toString()),
        }));

        if (selectableTypes.length > 0) {
            selectableTypes.push({ id: noTypeFilterId, name: translateString("none") });
        }

        return selectableTypes;
    }

    function getFilteredData(rows: Array<AnnualWheelActivityTemplateViewModel>): Array<AnnualWheelActivityTemplateViewModel> {
        return applyClientFilters(
            rows,
            [
                { id: "frameworks", candidate: (row) => row.mappedFrameworks.map((x) => x.id) },
                { id: "types", candidate: (row) => row.generalType?.toString() ?? noTypeFilterId },
                { id: "complianceAreas", candidate: (row) => row.complianceAreas.map((x) => x.id) },
                { id: "isPlanned", candidate: (row) => (row.isScheduled ? "1" : "0") },
                { id: "isCustom", candidate: (row) => (row.isCustom ? "1" : "0") },
            ],
            selectFilters,
            {},
            filterOperators
        )!;
    }

    function convertRowsToTableData(rows: Array<AnnualWheelActivityTemplateViewModel>): Array<AnnualWheelActivityTemplateTableModel> {
        return rows.map((row) => {
            return {
                ...row,
                complianceAreaString: row.complianceAreas.map((x) => x.name).join(", "),
                mappedFrameworkString: row.mappedFrameworks.map((x) => x.name).join(", "),
                isPlannedString: row.isScheduled ? translateString("plannedSingular") : translateString("notPlanned"),
                generalTypeString: row.generalType ? translateAnnualWheelActivityGeneralTypeEnum(row.generalType.toString()) : "",
            };
        });
    }
}

function getTemplateId(template: SelectedAnnualWheelActivityTemplate | undefined) {
    return template?.customTemplateId ?? template?.standardTemplateId;
}

function toSelectedTemplate(model: AnnualWheelActivityTemplateViewModel, isForContract: boolean): SelectedAnnualWheelActivityTemplate {
    return {
        name: model.name,
        interval: model.recurringInterval,
        recurringIntervalType: model.recurringIntervalType,
        complianceAreas: model.complianceAreas.map((x) => x.id),
        customId: model.customId,
        description: model.description ?? "",
        showTrialWarning: false,
        notPartOfCurrentPlan: false,
        planNow: false,
        priority: model.priority,
        customTemplateId: model.isCustom ? model.id : undefined,
        standardTemplateId: model.isCustom ? undefined : model.id,
        isProcessingActivityValidation: model.isProcessingActivityValidation,
        subTasks: model.subTasks,
        generalType: model.generalType,
        framework: undefined,
        notificationPeriodInDays: model.notificationPeriodInDays,
        // Only an obligation has ever taken these over from its template; an annual wheel activity never has.
        businessAreas: isForContract ? model.businessAreas : undefined,
        tags: isForContract ? model.tags : undefined,
        startDateRelativeConfig: isForContract ? model.startDateRelativeConfig : undefined,
        endDateRelativeConfig: isForContract ? model.endDateRelativeConfig : undefined,
    };
}
